import {
  Box,
  Button,
  Grid,
  Link,
  Paper,
  type SxProps,
  Typography,
} from '@mui/material';
import { Alert, SideLogo } from '@side/pantry';

const maxWidth = '687';

function CopyrightAndDisclaimer({ sx }: { sx?: SxProps }) {
  return (
    <Box
      sx={{
        textAlign: 'center',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginTop: 10,
        ...sx,
      }}
    >
      <Typography variant="caption" sx={{ margin: '0 auto' }}>
        Copyright © {new Date().getFullYear()} Side Inc.
      </Typography>
      <Box
        sx={{
          maxWidth: `${maxWidth}px`,
          margin: '0 auto',
          pt: 3,
          mb: { md: 0, xs: 15 },
        }}
      >
        <Typography sx={{ textAlign: 'center', fontSize: '12px' }}>
          Side uses tracking technology in connection with your usage of the
          Side App. By logging in and using the App, you consent to our use and
          this tracking technology in accordance with our{' '}
          <Link href="https://side.com/privacy-policy"> Privacy Policy </Link>
        </Typography>
      </Box>
    </Box>
  );
}

function LogoutNotice({
  hasExpiredSession,
  sx,
  'data-testid': dataTestId,
}: {
  hasExpiredSession: boolean;
  sx?: SxProps;
  'data-testid'?: string;
}) {
  return (
    <Alert
      sx={{
        marginLeft: 'auto',
        marginRight: 'auto',
        width: '100%',
        maxWidth: `${maxWidth}px`,
        marginBottom: 6,
        marginTop: { xs: 3, md: 0 },
        position: { xs: 'relative', md: undefined },
        ...sx,
      }}
      data-testid={dataTestId}
    >
      {hasExpiredSession
        ? 'Your session has expired. You will need to Sign In to proceed.'
        : 'You have been successfully logged out'}
    </Alert>
  );
}

type OktaLoginProps = {
  handleLogin: () => void;
  loading: boolean;
  logoutStatus: string;
  hasExpiredSession: boolean;
};

export function OktaLogin({
  handleLogin,
  loading = false,
  logoutStatus,
  hasExpiredSession,
}: OktaLoginProps) {
  return (
    <Grid
      container
      sx={{
        backgroundColor: { md: 'background.default', xs: 'background.paper' },
        flexDirection: 'column',
        justifyContent: { md: 'center' },
        alignItems: { md: 'center' },
        minHeight: { md: '100vh' },
      }}
    >
      {logoutStatus === 'success' && (
        <LogoutNotice
          // NOTE: We use test id instead of role because small screen alert is also in DOM (just not visible)
          data-testid="logout-notice"
          hasExpiredSession={hasExpiredSession}
          sx={{ display: { xs: 'none', md: 'flex' } }}
        />
      )}
      <Paper elevation={3}>
        <Grid
          container
          sx={{
            minHeight: { xs: '100vh', md: '400px' },
            justifyContent: { md: 'space-evenly', xs: 'space-between' },
            alignItems: { md: 'center', xs: 'end' },
            padding: 4,
            width: { md: `${maxWidth}px` },
          }}
        >
          {logoutStatus === 'success' && (
            <LogoutNotice
              data-testid="logout-notice-sm"
              hasExpiredSession={hasExpiredSession}
              sx={{ display: { xs: 'flex', md: 'none' } }}
            />
          )}
          <Grid textAlign="center" size={12}>
            <Box width={54} mt={0} mb={2} mx="auto">
              <SideLogo mode="dark" />
            </Box>
            <Typography variant="h2" component="h1" sx={{ marginBottom: 0 }}>
              Welcome to Side
            </Typography>
            <Typography
              variant="body1"
              gutterBottom
              fontWeight="fontWeightLight"
            >
              Sign in to your account
            </Typography>
            <Button
              variant="contained"
              loading={loading}
              loadingPosition="start"
              onClick={handleLogin}
              size="large"
              sx={{
                mt: { xs: 6, sm: 4 },
                mb: 4,
                width: { sm: 'auto', xs: '100%' },
                textTransform: 'uppercase',
              }}
            >
              Sign In
            </Button>
            <Typography variant="body1" gutterBottom>
              <Link
                role="link"
                href="https://sideagent.okta.com/signin/forgot-password"
              >
                Forgot Password?
              </Link>
            </Typography>
          </Grid>
          <CopyrightAndDisclaimer
            sx={{ display: { xs: 'block', md: 'none' } }}
          />
        </Grid>
      </Paper>
      <CopyrightAndDisclaimer sx={{ display: { xs: 'none', md: 'block' } }} />
    </Grid>
  );
}
